// src/componentsV2/Acciones/Cancelar.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Card, CardContent, Typography, Grid, Divider, Box, Alert, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Button, FormControl, InputLabel, MenuItem, Select
} from '@mui/material';
import Swal from 'sweetalert2';
import moment from 'moment';
import { setCargaDatos } from '../../Redux/V2/ContratoRedux';

const formatter = new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' });

const MOTIVOS_PENDIENTE = ['Lo quiere pensar', 'No asiste', 'No contesta', 'Otro'];
const MOTIVOS_PERDIDO  = [
  'Cliente no quiere servicio',
  'No puede pagarlo',
  'No aplica servicio',
  'Servicio no prestado por GD',
  'No asiste',
  'No contesta',
  'Otro',
];

const ticksFromHHmm = (hhmm) => {
  if (!hhmm) return 0;
  const [h, m] = hhmm.split(':').map(Number);
  const seconds = (h * 60 + m) * 60;
  // 1 segundo = 10,000,000 ticks
  return seconds * 10_000_000;
};

const fechaObjectFromISO = (isoDate) => {
  if (!isoDate) return null;
  const d = moment(isoDate, 'YYYY-MM-DD', true);
  if (!d.isValid()) return null;
  return {
    year: d.year(),
    month: d.month() + 1,   // moment: 0-11 -> .NET: 1-12
    day: d.date(),
    dayOfWeek: d.day(),     // domingo=0 ... sábado=6 (coincide con .NET)
  };
};

const Cancelar = ({ setHide }) => {
  const dispatch = useDispatch();

  const entrevista        = useSelector((s) => s.contratoV2.Entrevista);
  const defendido         = useSelector((s) => s.contratoV2.Defendido);
  const serviciosRedux    = useSelector((s) => s.contratoV2.contrato);
  const contratoCancelado = useSelector((s) => s.contratoV2.ContratoCancelado);

  const [atencion, setAtencion] = useState({
    Atendidopor: '',
    FechaAtencion: '',
    NombrePagador: '',
    RutPagador: '',
    TelefonoPagador: '',
    EmailPagador: '',
    DireccionPagador: '',
  });
  const [estado, setEstado] = useState('');
  const [comentarios, setComentarios] = useState('');
  const [fecha, setFecha] = useState('');  // YYYY-MM-DD
  const [hora, setHora] = useState('');    // HH:mm
  const [motivo, setMotivo] = useState('');
  const [finiquitado, setFiniquitado] = useState(false);

  useEffect(() => {
    if (entrevista?.contractId && (!serviciosRedux || serviciosRedux.length === 0)) {
      dispatch(setCargaDatos('contrato', 'get', entrevista.contractId));
    }
  }, [dispatch, entrevista?.contractId, serviciosRedux?.length]);

  const pickFechaEntrevista = (ent) => {
    const raw = ent?.fecha ?? ent?.createAt ?? ent?.date ?? ent?.dateContact ?? null;
    return raw ? moment(raw).format('DD-MM-YYYY HH:mm:ss') : '';
    // Si tu API expone otra propiedad para la fecha de atención, cámbiala aquí.
  };

  useEffect(() => {
    setAtencion({
      Atendidopor: entrevista?.atendidoPor ?? '',
      FechaAtencion: entrevista?.fechaAtencion
        ? moment(entrevista.fechaAtencion).format('DD-MM-YYYY HH:mm')
        : pickFechaEntrevista(entrevista),
      NombrePagador: [defendido?.firstName, defendido?.lastName].filter(Boolean).join(' '),
      RutPagador: defendido?.identifier ?? '',
      TelefonoPagador: defendido?.telephone ?? '',
      EmailPagador: defendido?.email ?? '',
      DireccionPagador: defendido?.address ?? '',
    });
  }, [entrevista, defendido]);

  useEffect(() => {
    if (contratoCancelado?.finiquitado === true || contratoCancelado?.status === 'finiquitado') {
      setFiniquitado(true);
    } else {
      setFiniquitado(false);
    }
  }, [contratoCancelado]);

  const lineaNegocio = entrevista?.lineaNegocioName || entrevista?.lineaNegocio || '';
  const serviciosTabla = useMemo(() => {
    return (serviciosRedux || []).map((s) => ({
      lineaNegocio,
      servicio: s.name,
      costo: Number(s.monto) || 0,
    }));
  }, [serviciosRedux, lineaNegocio]);

  const total = useMemo(
    () => serviciosTabla.reduce((acc, s) => acc + (Number(s.costo) || 0), 0),
    [serviciosTabla]
  );

  const handleReset = () => {
    setHide && setHide();
    setEstado('');
    setComentarios('');
    setFecha('');
    setHora('');
    setMotivo('');
  };

 // Devuelve el objeto fecha requerido por la API.
// - Si isoDate viene vacío o inválido -> objeto en ceros.
const buildFecha = (isoDate) => {
  if (!isoDate) {
    return { year: 0, month: 0, day: 0, dayOfWeek: 0 };
  }
  const d = moment(isoDate, 'YYYY-MM-DD', true);
  if (!d.isValid()) {
    return { year: 0, month: 0, day: 0, dayOfWeek: 0 };
  }
  return {
    year: d.year(),
    month: d.month() + 1,    // moment: 0-11  -> API: 1-12
    day: d.date(),
    dayOfWeek: d.day(),      // domingo=0 ... sábado=6 (igual que .NET)
  };
};

// 1 tick = 100 ns desde medianoche
const ticksFromHHmm = (hhmm) => {
  if (!hhmm) return 0;
  const [h, m] = hhmm.split(':').map(Number);
  const seconds = (h * 60 + m) * 60;
  return seconds * 10_000_000;
};
 

const handleSubmit = async () => {
  // Normaliza fecha/hora al formato que espera el backend
  // - fecha: "YYYY-MM-DD" (DateOnly?)
  // - hora:  "HH:mm:ss"   (TimeSpan?)
  const fechaValue = fecha
    ? moment(fecha, "YYYY-MM-DD", true).isValid()
      ? moment(fecha, "YYYY-MM-DD").format("YYYY-MM-DD")
      : null
    : null;

  const horaValue = hora ? `${hora}:00` : null; // viene como "HH:mm"

  const payload = {
    customerInterviewId:
      entrevista?.id ?? Number(sessionStorage.getItem("customerInterviewId")),
    estado,
    motivo,
    comentario: comentarios, // nombre correcto según tu DTO
    fecha: fechaValue,       // string o null
    hora: horaValue,         // string o null
    createdBy:
      entrevista?.atendidoPor ??
      sessionStorage.getItem("usuarioRut") ??
      "sistema",
  };

  if (!payload.estado) {
    Swal.fire({
      position: "center",
      icon: "error",
      title: "Seleccione la razón.",
      timer: 4000,
    });
    return;
  }

  if (!payload.motivo) {
    Swal.fire({
      position: "center",
      icon: "error",
      title: "Seleccione el motivo.",
      timer: 4000,
    });
    return;
  }

  const sfireHB = Swal.fire({
    title: "Guardando cancelación...",
    allowOutsideClick: false,
    didOpen: () => Swal.showLoading(),
  });

  const exito = await dispatch(setCargaDatos("ContratoCancelado", "post", payload));

  sfireHB.close();

  Swal.fire({
    position: "center",
    icon: exito ? "success" : "error",
    title: exito ? "Cancelación guardada correctamente" : "No fue posible guardar la cancelación",
    timer: 3500,
  });

  if (exito) {
    dispatch(setCargaDatos("ContratoCancelado", "get", payload.customerInterviewId));
    handleReset();
  }
};


  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: '40px', p: 4 }}>
      <Card sx={{ boxShadow: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" gutterBottom>Cancelar contrato</Typography>
            <Button variant="outlined" onClick={handleReset}>Volver</Button>
          </Box>

          <Alert className="mt-2 mb-4" severity="warning" hidden={!finiquitado}>
            Contrato Finiquitado.
          </Alert>

          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography><strong>Atendido por:</strong> {atencion.Atendidopor}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography><strong>Fecha Atención:</strong> {atencion.FechaAtencion}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography><strong>Nombre:</strong> {atencion.NombrePagador}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography><strong>RUT:</strong> {atencion.RutPagador}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography><strong>Email:</strong> {atencion.EmailPagador}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography><strong>Teléfono:</strong> {atencion.TelefonoPagador}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography><strong>Dirección:</strong> {atencion.DireccionPagador}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography>
                <strong>Monto Acordado Contrato: </strong> {formatter.format(total || 0)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Paper} sx={{ boxShadow: 3, mt: 4 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#C8BFE7' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>LINEA DE NEGOCIO</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>SERVICIO</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>COSTO SERVICIO</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {serviciosTabla.map((row, i) => (
              <TableRow key={i}>
                <TableCell>{row.lineaNegocio}</TableCell>
                <TableCell>{row.servicio}</TableCell>
                <TableCell>{formatter.format(Number(row.costo) || 0)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={2}></TableCell>
              <TableCell>
                <Typography sx={{ fontWeight: 'bold' }}>: Total Servicios</Typography>
                <Typography>{formatter.format(total || 0)}</Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Card sx={{ mt: 4, boxShadow: 3 }}>
        <CardContent>
          <Box sx={{ maxWidth: 400, mx: 'auto', mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6">Formulario de Cancelación</Typography>

            <FormControl fullWidth>
              <InputLabel id="cancelar-razon-label">Razón</InputLabel>
              <Select
                id="cancelar-razon-select"
                labelId="cancelar-razon-label"
                value={estado}
                label="Razón"
                onChange={(e) => setEstado(e.target.value)}
                required
                MenuProps={{ disablePortal: true, slotProps: { paper: { sx: { zIndex: 2000 } } } }}
              >
                <MenuItem value="" disabled>Seleccione...</MenuItem>
                <MenuItem value="pendiente">Pendiente</MenuItem>
                <MenuItem value="perdido">Perdido</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="cancelar-motivo-label">Motivo</InputLabel>
              <Select
                id="cancelar-motivo-select"
                labelId="cancelar-motivo-label"
                value={motivo}
                label="Motivo"
                onChange={(e) => setMotivo(e.target.value)}
                required
                MenuProps={{ disablePortal: true, slotProps: { paper: { sx: { zIndex: 2000 } } } }}
              >
                <MenuItem value="" disabled>Seleccione...</MenuItem>
                {(estado === 'perdido' ? MOTIVOS_PERDIDO : MOTIVOS_PENDIENTE).map((m) => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {estado === 'pendiente' && (
              <>
                <TextField
                  label="Fecha"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                />
                <TextField
                  label="Hora"
                  type="time"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={hora}
                  onChange={(e) => setHora(e.target.value)}
                />
              </>
            )}

            <TextField
              label="Comentarios"
              multiline
              rows={3}
              fullWidth
              value={comentarios}
              onChange={(e) => setComentarios(e.target.value)}
              required
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button variant="contained" color="primary" onClick={handleSubmit}>
                Confirmar
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Cancelar;
